import React, { useState, useEffect, useCallback } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { parse, format } from "date-fns";
import { getConfirmDeleteDialog } from "../Components/confirmDeleteDialog";
import {
  CssBaseline,
  Box,
  Button,
  Dialog,
  FormHelperText,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Alert,
  Snackbar,
  MenuItem,
  Typography,
  OutlinedInput,
  Grid,
  ThemeProvider,
  Select,
  FormControl,
  InputLabel,
  } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FlagIcon from "@mui/icons-material/Flag";
import { ReactComponent as DeleteIcon } from "../Icons/Delete.svg";
import CloseIcon from "@mui/icons-material/Close";
import Table_theme from "../Themes/TableTheme";
import { useCurrentUser } from "../utils/useCurrentUser";
import { formatDateLocal, formatDateLocal_TF } from "../utils/dateUtils";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const empProjectsFields = [
  "employee_id",
  "cl_name",
  "proj_id",
  "assigned_from",
  "assigned_to",
  "projectmanagerid",
  "technicalleadid",
];
const popupFields = [
  "cl_id",
  "cl_name",
  "proj_id",
  "assigned_from",
  "assigned_to",
];
const initialEmpPjctState = {
  cl_id: "",
  proj_id: "",
  assigned_from: "",
  assigned_to: "",
};
const empfieldLabels = {
  emp_proj_id: "Employee Project ID",
  cl_id: "Client",
  cl_name: "Client Name",
  employee_id: "Employee",
  proj_id: "Project",
  assigned_from: "Assigned From",
  assigned_to: "Assigned To",
  allocation_percentage: "Allocation Percentage",
  projectmanagerid: "Project Manager",
  technicalleadid: "Technical Lead",
};
const EmployeeProjectDialogue = ({open, onClose, employeeId, employeeName }) => {
  const { loading, userId, empId, boId, roles, hasPermission } =
    useCurrentUser();
  const [empPjct, setEmpPjct] = useState([]);
  const [alertMsg, setAlertMsg] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [newEmpPjct, setNewEmpPjct] = useState({
    bo_id: boId,
    employee_id: empId,
    proj_id: "",
    assigned_from: "",
    assigned_to: "",
    allocation_percentage: "",
  });
  const [filters, setFilters] = useState({});
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [clients, setClients] = useState([]);
  const [empProjEdit, setEditEmpProj] = useState({});
  const [activeForm, setActiveForm] = useState(null);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [projects, setProjects] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isProjectsLoading, setIsProjectsLoading] = useState(false);
  const bo_id = boId;
  const fetchempPjct = useCallback(async () => {
    const cleanFilters = Object.fromEntries(
      Object.entries(filters).filter(([_, v]) => v && v.trim() !== "")
    );
    try {
      setIsLoading(true);
      const params = new URLSearchParams({
        employeeId,
        bo_id,
      });
      const response = await fetch(
        `${BASE_URL}/employee_projects/paginate/empList?${params}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Fetch failed");
      setEmpPjct(data.data);
      setTotalCount(data.total);
    } catch (err) {
      console.error("Error fetching employee projects:", err);
      setSnackbarMsg(err.message || "Failed to fetch employee projects");
      setShowSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  }, [page, filters, search, bo_id]);

  const fetchClientAndProjects = useCallback(async () => {
    try {
      const response = await fetch(
        `${BASE_URL}/employee_projects/${boId}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Fetch failed");
      setClients(data.clients);
      setProjects(data.projects);
    } catch (err) {
      console.error("Error fetching projects:", err);
      setAlertMsg(err.message || "Failed to fetch projects");
      setAlertSeverity("error");
    }
  }, []);

  useEffect(() => {
    fetchClientAndProjects();
  }, [fetchClientAndProjects]);

  useEffect(() => {
    if (open && projects.length > 0) {
      fetchempPjct();
    }
  }, [open, projects, fetchempPjct]);

  useEffect(() => {
    if (alertMsg) {
      const timer = setTimeout(() => {
        setAlertMsg("");
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [alertMsg]);

  const renderField = (field, state, setState, isEditMode = false) => {
    const handleChange = (e) => {
      const { name, value } = e.target;
      let processedValue = value;
      if (field === "allocation_percentage")
        processedValue = parseFloat(value) || "";
      else if (field === "assigned_from" || field === "assigned_to")
        processedValue = value;
      setState((prev) => ({
        ...prev,
        [name]: processedValue,
      }));
      //  Clear inline error for the current field
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    };
    if (field === "cl_id") {
      return (
        <FormControl
          sx={{
            width: "250px",
            "& .MuiOutlinedInput-root": {
              height: "35px",
              fontSize: "12px",
              "& .MuiInputBase-input": { padding: "10px 14px" },
            },
          }}
          margin="none"
          required
          error={!!errors.cl_id}
        >
          <InputLabel
            shrink
            sx={{
              fontSize: "12px",
              position: "absolute",
              transform: "translate(14px, -8px) scale(0.75)", 
            }}
          >
            Client
          </InputLabel>
          <Select
            value={state.cl_id || ""}
            name="cl_id"
            onChange={(e) => {
              const value = e.target.value;
              setState((prev) => ({
                ...prev,
                cl_id: e.target.value,
                proj_id: "",
              }));
              // Clear inline error for client
              setErrors((prev) => ({
                ...prev,
                cl_id: "",
              }));
            }}
            input={<OutlinedInput notched label="Client " />}
            disabled={isLoading}
          >
            {clients.map((client) => (
              <MenuItem key={client.cl_id} value={client.cl_id} sx={{ fontSize: "12px" }}>
                {client.cl_name}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText sx={{ minHeight: "18px" }}>
            {errors.cl_id || " "}
          </FormHelperText>
        </FormControl>
      );
    }

    if (field === "proj_id") {
      const filteredProjects = projects.filter(
        (proj) => proj.cl_id === state.cl_id
      );
      return (
        <FormControl
          sx={{
            width: "250px",
            "& .MuiOutlinedInput-root": {
              height: "35px",
              fontSize: "12px",
              "& .MuiInputBase-input": { padding: "10px 14px" },
            },
          }}
          margin="none"
          required
          error={!!errors.proj_id}
        >
          <InputLabel
            shrink
            sx={{
              fontSize: "12px",
              transform: "translate(14px, -8px) scale(0.75)",
              }}
          >
            Project
          </InputLabel>
          <Select
            value={state.proj_id || ""}
            onChange={(e) => {
              handleChange(e);
              setErrors((prev) => ({ ...prev, proj_id: "" }));
            }}
            name="proj_id"
            input={<OutlinedInput notched label="Project" />}
            disabled={isLoading || !state.cl_id}
          >
            {filteredProjects.map((proj) => (
              <MenuItem key={proj.proj_id} value={proj.proj_id} sx={{ fontSize: "12px" }}>
                {proj.project_name}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText sx={{ minHeight: "18px" }}>
            {errors.proj_id || " "}
          </FormHelperText>
        </FormControl>
      );
    }
    if (field === "assigned_from" || field === "assigned_to") {
      return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label={empfieldLabels[field]}
            value={state[field] ? new Date(state[field]) : null}
            onChange={(newValue) => {
              setState((prev) => ({
                ...prev,
                [field]: newValue,
              }));
              setErrors((prev) => ({ ...prev, [field]: "" }));
            }}
            format="MM/dd/yyyy"
            slotProps={{
              textField: {
                required: true,
                error: false,
                name: field,
                placeholder: "MM/DD/YYYY",
                error: !!errors[field],
                helperText: errors[field] || " ",
                InputLabelProps: { shrink: true, sx: { fontSize: "12px" } },
                InputProps: {
                  sx: {
                    paddingTop: "2px",
                    paddingBottom: "2px",
                    fontSize: "12px",
                    height: 35,
                    backgroundColor: "#fafafa",
                  },
                },
                sx: {
                  width: "200px", // input box only
                },
                margin: "none",
              },
              popper: {
                disablePortal: false,
                placement: "bottom-start",
              },
            }}
          />
        </LocalizationProvider>
      );
    }
  };
  const handleAddEmpPjct = async () => {
    const newErrors = {};

    if (!newEmpPjct.cl_id) {
      newErrors.cl_id = "Client is required";
    }
    if (!newEmpPjct.proj_id) {
      newErrors.proj_id = "Project is required";
    }
    if (!newEmpPjct.assigned_from || !newEmpPjct.assigned_to) {
      newErrors.assigned_from = "Please select from date";
      newErrors.assigned_to = "Please select to date";
    }
    const fromDate = newEmpPjct.assigned_from
      ? formatDateLocal_TF(newEmpPjct.assigned_from)
      : null;
    const toDate = newEmpPjct.assigned_to
      ? formatDateLocal_TF(newEmpPjct.assigned_to)
      : null;
    //  Show only in Snackbar if fromDate > toDate
    if (fromDate && toDate && fromDate > toDate) {
      setSnackbarMsg(
        "Assigned From date must be before or equal to Assigned To date"
      );
      setShowSnackbar(true);
      // Do not set inline errors for these fields
      return;
    }
    // If there are any errors, show inline and stop here
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    try {
      const response = await fetch(`${BASE_URL}/employee_projects`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...newEmpPjct,
          employee_id: employeeId,
          assigned_from: fromDate, // yyyy-mm-dd
          assigned_to: toDate, //  yyyy-mm-dd
          bo_id: boId,
          created_by: empId,
          modified_by: empId,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Add failed");
      setNewEmpPjct({
        bo_id: boId,
        employee_id: "",
        proj_id: "",
        assigned_from: "",
        assigned_to: "",
        allocation_percentage: "",
      });
      setAlertMsg("Project assigned to the employee successfully!");
      setAlertSeverity("success");
      fetchempPjct();
      setActiveForm(null);
    } catch (err) {
      console.error("Add failed:", err);
      setAlertMsg(`Failed to add record: ${err.message}`);
      setAlertSeverity("error");
    }
  };
  const handleEditEmpPjct = (empPjct) => {
    setEditEmpProj({
      ...empPjct,
      employee_id: empPjct.employee_id || employeeId,
    });
    setActiveForm("edit");
  };
  const handleDeleteEmpPjct = async (emp_proj_id) => {
    try {
      const response = await fetch(
        `${BASE_URL}/employee_projects/${emp_proj_id}/${bo_id}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Delete failed");
      setAlertMsg(
        `${selectedProject.employee_name} - ${selectedProject.project_name} has been deleted successfully`
      );
      setAlertSeverity("success");

      fetchempPjct();
    } catch (err) {
      console.error("Delete failed:", err);
      setAlertMsg(`Failed to delete record: ${err.message}`);
      setAlertSeverity("error");
    }
  };
  const confirmDelete = (project) => {
    if (!project || !project.emp_proj_id) {
      console.error("Invalid project object");
      return;
    }
    setSelectedProject(project);
    setDeleteDialogOpen(true);
  };
  const handleUpdateEmpPjct = async () => {
    const fromDate = formatDateLocal_TF(empProjEdit.assigned_from);
    const toDate = formatDateLocal_TF(empProjEdit.assigned_to);
    if (fromDate > toDate) {
      setSnackbarMsg(
        "Assigned From date must be less than or equal to Assigned To date"
      );
      setShowSnackbar(true);
      return;
    }
    try {
      const response = await fetch(
        `${BASE_URL}/employee_projects/${empProjEdit.emp_proj_id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...empProjEdit,
            assigned_from: fromDate, // yyyy-mm-dd
            assigned_to: toDate, //  yyyy-mm-dd
            bo_id: boId,
            modified_by: empId,
          }),
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Update failed");
      setEditEmpProj({});
      setAlertMsg("Project updated successfully!");
      setAlertSeverity("success");
      fetchempPjct();
      setActiveForm(null);
    } catch (err) {
      console.error("Edit failed:", err);
      setAlertMsg(`Failed to update record: ${err.message}`);
      setAlertSeverity("error");
    }
  };
  const handleFlagClick = async (emp_proj_id, currentFlag) => {
    const newFlag = currentFlag === "Y" ? "N" : "Y";
    try {
      const response = await fetch(
        `${BASE_URL}/employee_projects/${emp_proj_id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            bo_id: boId,
            delete_flag: newFlag,
            modified_by: empId,
          }),
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Flag update failed");
      setAlertMsg("Toggle flag updated successfully..");
      setAlertSeverity("success");
      fetchempPjct();
    } catch (err) {
      console.error("Error updating flag:", err);
      setAlertMsg(`Failed to update record: ${err.message}`);
      setAlertSeverity("error");
    }
  };
  const handleCancel = () => {
    setEditEmpProj({});
    setActiveForm(null);
  };
  const handleAddButtonClick = () => {
    setNewEmpPjct(initialEmpPjctState);
    setErrors({});
    setActiveForm("add");
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle
        sx={{
          borderRadius: "12px",
          background: "linear-gradient(45deg, #e6f0fa, #ffffff)",
          p: 2,
          fontSize: "14px",
          fontWeight: 550,
        }}
      >
        Project Details for Employee:{employeeName}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 5 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {!activeForm && (
          <Box mb={2} display="flex" justifyContent="flex-end">
            {hasPermission("Employee Projects", "Edit") && (
              <Button
                variant="contained"
                onClick={handleAddButtonClick}
                sx={{ height: "35px", mt: 1, backgroundColor: "#2DB0E4" }}
              >
                Add Project
              </Button>
            )}
          </Box>
        )}
        {activeForm === "add" && (
          <Box mb={2}>
            <Grid container spacing={1} mt={2} alignItems="flex-start">
              {popupFields.map(
                (field) =>
                  field !== "emp_proj_id" && (
                    <Grid item xs={12} sm={6} key={field}>
                      {renderField(field, newEmpPjct, setNewEmpPjct)}
                    </Grid>
                  )
              )}
              <Grid
                item
                xs={12}
                sm={6}
                sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}
              >
                <Button
                  variant="contained"
                  onClick={handleAddEmpPjct}
                  sx={{ height: "35px", backgroundColor: "#2DB0E4" }}
                >
                  Save
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setActiveForm(null);
                    setErrors({});
                  }}
                  sx={{
                    height: "35px",
                    width: "90px",
                    color: "#555",
                    borderColor: "#ccc",
                  }}
                >
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
        {activeForm === "edit" && (
          <Box mb={2}>
            <Grid container spacing={1} mt={2}>
              {popupFields.map((field) => (
                <Grid item xs={12} sm={6} key={field}>
                  {renderField(field, empProjEdit, setEditEmpProj, true)}
                </Grid>
              ))}
              <Grid
                item
                xs={12}
                sm={6}
                sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}
              >
                <Button
                  variant="contained"
                  onClick={handleUpdateEmpPjct}
                  sx={{
                    height: "35px",
                    width: "100px",
                 backgroundColor:"#2DB0E4",
                    "&:hover": { backgroundColor:"#2DB0E4" },
                  }}
                >
                  Update
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  sx={{
                    height: "35px",
                    width: "100px",
                    color: "#555",
                    borderColor: "#ccc",
                  }}
                >
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
        {alertMsg && (
          <Box mb={2}>
            <Alert
              onClose={() => setAlertMsg("")}
              variant="standard"
              sx={{
                color: alertSeverity === "success" ? "#2E9F50" : "#FF0C0C",
                fontWeight: 500,
                backgroundColor: "#fff",
              }}
            >
              {alertMsg}
            </Alert>
          </Box>
        )}
        {getConfirmDeleteDialog({
          open: deleteDialogOpen,
          onClose: () => {
            setDeleteDialogOpen(false);
            setSelectedProject(null);
          },
          onConfirm: () => {
            handleDeleteEmpPjct(selectedProject.emp_proj_id);
            setDeleteDialogOpen(false);
            setSelectedProject(null);
          },
          title: "Confirm Deletion",
          contentText: selectedProject
            ? `Are you sure you want to remove ${selectedProject.employee_name} from the project ${selectedProject.project_name}?`
            : "Are you sure you want to delete this Project?",
        })}

        <ThemeProvider theme={Table_theme}>
          <CssBaseline />
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Actions</TableCell>
                  {empProjectsFields.map((field) => (
                    <TableCell key={field}>{empfieldLabels[field]}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {empPjct.length > 0 ? (
                  empPjct.map((empPjct) => (
                    <TableRow key={empPjct.emp_proj_id}>
                      <TableCell
                        sx={{ fontSize: "0.7rem", textAlign: "center" }}
                      >
                        <Box display="flex" gap={0.5} justifyContent="center">
                          <Tooltip
                            title={
                              hasPermission("Employee Projects", "Edit")
                                ? "Edit"
                                : "No permission"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  hasPermission("Employee Projects", "Edit") &&
                                  handleEditEmpPjct(empPjct)
                                }
                                disabled={
                                  !hasPermission("Employee Projects", "Edit")
                                }
                                sx={{
                                  color: hasPermission(
                                    "Employee Projects",
                                    "Edit"
                                  )
                                    ? "#2DB0E4"
                                    : "#ccc",
                                }}
                              >
                                <EditOutlinedIcon sx={{ fontSize: "16px" }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <span>
                            <Tooltip
                              title={
                                hasPermission("Employee Projects", "Delete")
                                  ? "Delete"
                                  : "No permission"
                              }
                            >
                              <IconButton
                                size="small"
                                onClick={() =>
                                  hasPermission(
                                    "Employee Projects",
                                    "Delete"
                                  ) && confirmDelete(empPjct)
                                }
                                disabled={
                                  !hasPermission("Employee Projects", "Delete")
                                }
                              >
                                <DeleteIcon
                                  sx={{
                                    color: hasPermission(
                                      "Employee Projects",
                                      "Delete"
                                    )
                                      ? "red"
                                      : "#ccc",
                                    width: 15,
                                    height: 15,
                                  }}
                                />
                              </IconButton>
                            </Tooltip>
                          </span>
                          <Tooltip
                            title={
                              hasPermission("Employee Projects", "Edit")
                                ? "Toggle Delete Flag"
                                : "No permission"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  hasPermission("Employee Projects", "Edit") &&
                                  handleFlagClick(
                                    empPjct.emp_proj_id,
                                    empPjct.delete_flag
                                  )
                                }
                                disabled={
                                  !hasPermission("Employee Projects", "Edit")
                                }
                              >
                                <FlagIcon
                                  sx={{
                                    color:
                                      empPjct.delete_flag === "N"
                                        ? "#2E9F50"
                                        : "#FF0C0C",
                                    width: 17,
                                    height: 17,
                                  }}
                                />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      {empProjectsFields.map((field) => (
                        <TableCell
                          key={field}
                          sx={{ fontSize: "0.7rem", textAlign: "left" }}
                        >
                          {field === "proj_id"
                            ? empPjct.project_name || empPjct.proj_id
                            : field === "employee_id"
                            ? empPjct.employee_name || empPjct.employee_id
                            : field === "assigned_from" ||
                              field === "assigned_to"
                            ? formatDateLocal(empPjct[field])
                            : field === "projectmanagerid"
                            ? empPjct.projectmanager_name ||
                              empPjct.projectmanagerid ||
                              "-"
                            : field === "technicalleadid"
                            ? empPjct.technicallead_name ||
                              empPjct.technicalleadid ||
                              "-"
                            : empPjct[field] || "-"}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={empProjectsFields.length + 1}
                      align="center"
                    >
                      No project records found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </ThemeProvider>
      </DialogContent>
      <DialogActions sx={{ p: "8px 24px", justifyContent: "flex-end" }}>
        <Button
          onClick={onClose}
          variant="outlined"
          color="inherit"
          sx={{ mr: 1, color: "#555", borderColor: "#ccc" }}
        >
          Close
        </Button>
      </DialogActions>
      <Snackbar
        open={showSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowSnackbar(false)}
          severity={snackbarMsg.includes("Failed") ? "error" : "success"}
          sx={{ width: "100%" }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};
export default EmployeeProjectDialogue;
